import type { FC } from "react";
import { Breadcrumb, Container } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { TopicAssignmentDetailHeader, Loading } from "components";
import IconArrowBack from "components/Icons/IconArrowBack/IconArrowBack";
import { AssignmentDetailPractice } from "features";

import { useAppSelector } from "store/hooks";
import { getAssignmentsGlobalParams, getAssignmentsListParams } from "store/slices/queryParams";

import { useUserAssignmentDetail } from "utils/hooks/useUserAssignmentDetail";
import { resolveAssignmentReturnPath } from "utils/common/assignmentDetail";

interface Props {
  themeId?: string;
  userThemeId?: string;
  assignmentId: string;
}

const AssignmentDetailContent: FC<Props> = ({ themeId, userThemeId, assignmentId }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation(["common"]);
  const globalQueryParams = useAppSelector(getAssignmentsGlobalParams);
  const listQueryParams = useAppSelector(getAssignmentsListParams(themeId));

  const { assignmentData, isLoading, isUnavailable, title } = useUserAssignmentDetail(assignmentId);

  const getBackUrl = () => {
    const returnPath = resolveAssignmentReturnPath(location.state);
    if (returnPath) return returnPath;

    if (themeId === undefined) {
      let urlSlug = "/ulohy";
      let first = true;

      for (const [paramName, paramValue] of Object.entries(globalQueryParams)) {
        if (!first) {
          urlSlug += `&${paramName}=${paramValue}`;
        }

        if (first) {
          urlSlug += `?${paramName}=${paramValue}`;
          first = false;
        }
      }

      return urlSlug;
    }

    let urlSlug = `/ulohy/${themeId}`;
    if (listQueryParams === null || listQueryParams === undefined) return urlSlug;

    let first = true;

    for (const [paramName, paramValue] of Object.entries(listQueryParams)) {
      if (!first) {
        urlSlug += `&${paramName}=${paramValue}`;
      }

      if (first) {
        urlSlug += `?${paramName}=${paramValue}`;
        first = false;
      }
    }

    return urlSlug;
  };

  const navigateAssignment = (newAssignmentId: string) => {
    if (themeId === undefined) {
      navigate(`/ulohy/detail/${newAssignmentId}`);
    } else {
      navigate(`/ulohy/${themeId}/${newAssignmentId}`);
    }
  };

  return (
    <>
      <div className={"page-header page-header--has-tabs"}>
        <Container className={"container-mw-md"}>
          <Breadcrumb>
            <li className={"breadcrumb-item"}>
              <Link to={getBackUrl()}>
                <IconArrowBack />
                {t("back", { ns: "common" })}
              </Link>
            </li>
          </Breadcrumb>

          <TopicAssignmentDetailHeader
            assignmentId={assignmentData?.assignment?.id ?? ""}
            userAssignmentId={assignmentId}
            themeId={userThemeId ?? "searched"}
            onNavigate={navigateAssignment}
            title={title}
          />
        </Container>
      </div>

      {isLoading ? (
        <Loading />
      ) : (
        <AssignmentDetailPractice
          assignment={assignmentData}
          userAssignmentId={assignmentId}
          isUnavailable={isUnavailable}
        />
      )}
    </>
  );
};

export default AssignmentDetailContent;
